import { keepPreviousData, useQuery } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";
import { LIVE_LEDGER_ENRICHMENT_PARAMS } from "byzantine/src/TransactionEnrichmentSource";

import { toEndOfDay, toStartOfDay } from "../helpers";

export interface TransactionsListResponse {
  transactions: API.Transaction[];
  meta: { total: number };
  links: { next: string | null; previous: string | null };
}

export interface UseTransactionsListOptions {
  page: number;
  perPage: number;
  accountIds?: string[];
  categories?: string[];
  baiCodes?: string[];
  methods?: string[];
  amountMin?: number;
  amountMax?: number;
  settledFrom?: string;
  settledTo?: string;
  type?: string;
  search?: string;
  includeReceipts?: boolean;
  /** Skips the fetch entirely, e.g. while a locked account scope is transiently empty. */
  enabled?: boolean;
}

export interface UseTransactionsListResult {
  transactions: API.Transaction[];
  total: number;
  isLoading: boolean;
  /**
   * True whenever a request is in flight, including a page change.
   * `isLoading` covers only the first load: with `keepPreviousData` a page change keeps
   * the previous rows and leaves `isLoading` false, so anything that needs to know a
   * page is arriving has to read this instead.
   */
  isFetching: boolean;
  error: string | null;
  refetch: () => void;
}

export const TRANSACTIONS_LIST_QUERY_KEY = "ledgerTransactionsList";

const EMPTY_RESPONSE: TransactionsListResponse = {
  transactions: [],
  meta: { total: 0 },
  links: { next: null, previous: null },
};

// Parity with the per-account sidebar's Transaction.sortByPostedDate: pending
// rows always sort ahead of settled ones. Array.prototype.sort is a stable
// sort, so ties (pending-vs-pending, settled-vs-settled) keep the order the
// API returned them in.
const sortPendingFirst = (transactions: API.Transaction[]): API.Transaction[] =>
  [...transactions].sort((a, b) => {
    if (!a.settled_at && b.settled_at) return -1;
    if (a.settled_at && !b.settled_at) return 1;
    return 0;
  });

const isNotFound = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { response?: { status?: number } }).response?.status === 404;

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return "An unexpected error occurred";
};

export const useTransactionsList = ({
  page,
  perPage,
  accountIds,
  categories,
  baiCodes,
  methods,
  amountMin,
  amountMax,
  settledFrom,
  settledTo,
  type,
  search,
  includeReceipts = false,
  enabled = true,
}: UseTransactionsListOptions): UseTransactionsListResult => {
  const endpoint = accountIds?.length
    ? `accounts/${accountIds.join(",")}/transactions`
    : "transactions";

  const fetchPage = async (): Promise<TransactionsListResponse> => {
    try {
      const params: Record<string, string | number> = {
        page,
        per_page: perPage,
        filter: "hidden",
        include_bai2: "true",
        include_account_type: "true",
        ...LIVE_LEDGER_ENRICHMENT_PARAMS,
      };
      if (categories?.length) {
        params.category = categories.join(",");
      }
      if (baiCodes?.length) {
        params.bai_codes = baiCodes.join(",");
      }
      if (methods?.length) {
        params.method = methods.join(",");
      }
      if (amountMin !== undefined) {
        params.amount_min = amountMin;
      }
      if (amountMax !== undefined) {
        params.amount_max = amountMax;
      }
      if (settledFrom) {
        params.settled_from = toStartOfDay(settledFrom);
      }
      if (settledTo) {
        params.settled_to = toEndOfDay(settledTo);
      }
      if (type !== undefined) {
        params.type = type;
      }
      if (search) {
        params.q = search;
      }
      // Opting in costs a prefetch and a signed thumbnail url per row, so it
      // stays off when the column is hidden.
      if (includeReceipts) {
        params.include_receipts = "true";
      }

      // rawResponse keeps the 404 status; ApiHttp otherwise flattens it to a permissions string.
      const response = await ApiHttp.fetch<{ data: TransactionsListResponse }>(
        endpoint,
        { rawResponse: true },
        params
      );
      return {
        ...response.data,
        transactions: sortPendingFirst(response.data.transactions),
      };
    } catch (error) {
      // This endpoint 404s on empty result sets.
      if (isNotFound(error)) {
        return EMPTY_RESPONSE;
      }
      throw error;
    }
  };

  const { data, isLoading, isFetching, error, refetch } =
    useQuery<TransactionsListResponse>({
      queryKey: [
        TRANSACTIONS_LIST_QUERY_KEY,
        page,
        perPage,
        accountIds?.join(",") ?? "",
        categories?.join(",") ?? "",
        baiCodes?.join(",") ?? "",
        methods?.join(",") ?? "",
        amountMin ?? "",
        amountMax ?? "",
        settledFrom ?? "",
        settledTo ?? "",
        type ?? "",
        search ?? "",
        includeReceipts,
      ],
      queryFn: fetchPage,
      // Prior page stays visible while the next page resolves.
      placeholderData: keepPreviousData,
      retry: false,
      enabled,
    });

  return {
    transactions: data?.transactions ?? [],
    total: data?.meta.total ?? 0,
    isLoading,
    isFetching,
    error: error ? extractErrorMessage(error) : null,
    refetch,
  };
};
